import React, { useEffect } from "react";
import "./Assistant.css";

const LOOM_ID = "9bf896cf72344c0ebbb79281201ed6fe";
const EMAIL = "raja@rajaomar.me";

const MAILTO =
  `mailto:${EMAIL}?subject=` +
  encodeURIComponent("Sourced - free build on our documents") +
  "&body=" +
  encodeURIComponent(
    "Hi Raja,\n\n" +
      "Company:\n" +
      "Documents we'd start with:\n" +
      "Questions we'd want it to answer:\n\n"
  );

const PRINCIPLES = [
  {
    title: "Every answer is cited",
    body:
      "Each response carries the exact line, in the exact document, it came from. You verify it in one click instead of taking it on trust.",
  },
  {
    title: "It refuses to guess",
    body:
      "If the answer isn't in your documents, it says so. No confident invention - which is the one failure mode that makes generative AI unusable for professional work.",
  },
  {
    title: "It searches meaning, not keywords",
    body:
      "It finds the clause that says the thing even when it's worded nothing like your question, and it looks across every document at once rather than one file at a time.",
  },
];

const DATA_POINTS = [
  {
    title: "It deploys inside your environment",
    body:
      "Your documents stay where they already live. Nothing is copied out to a third party, and I never hold a copy.",
  },
  {
    title: "Never used to train a model",
    body:
      "Your files are not training data. Not now, not later, not in aggregate.",
  },
  {
    title: "It only indexes what you point it at",
    body:
      "No crawling, no standing access to your systems. The corpus is exactly the set of documents you hand over, and nothing outside it is reachable.",
  },
  {
    title: "Every question and answer is logged",
    body:
      "A complete record of what was asked and what came back, for whenever you need to account for it.",
  },
];

const STEPS = [
  {
    n: "01",
    title: "You pick the documents",
    body:
      "Start with material you've already made public. Nothing confidential, no access to your systems, no agreement needed.",
  },
  {
    n: "02",
    title: "I build it",
    body:
      "A few days. You get a private link and can ask it anything you like, including the questions you expect it to fail.",
  },
  {
    n: "03",
    title: "You decide",
    body:
      "If it earns its place on your own material, we talk about the real archive. If it doesn't, there's no cost and nothing lost.",
  },
];

const Assistant = () => {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Sourced - answers from your own documents";

    // Reachable by direct link only; keep it out of search results.
    const robots = document.createElement("meta");
    robots.name = "robots";
    robots.content = "noindex, nofollow";
    document.head.appendChild(robots);

    return () => {
      document.title = previousTitle;
      document.head.removeChild(robots);
    };
  }, []);

  return (
    <div className="sc-page">
      <section className="sc-hero">
        <span className="sc-badge">
          <span className="sc-badge-dot"></span>
          Sourced
        </span>

        <h1 className="sc-h1">
          <span className="sc-h1-line">Answers from your own documents,</span>
          <span className="sc-h1-accent">with the source line attached.</span>
        </h1>

        <p className="sc-lede">
          Sourced is a private assistant that answers questions from a firm's own
          files - contracts, SOPs, policies, past work, candidate records. Every
          answer cites the exact line it came from. When something isn't in the
          documents, it says so instead of guessing.
        </p>

        <div className="sc-cta-row">
          <a className="sc-btn-primary" href="#demo">
            Watch the 60-second demo
          </a>
          <a className="sc-btn-ghost" href={MAILTO}>
            Request a free build
          </a>
        </div>
      </section>

      <div className="sc-rule"></div>

      <section className="sc-section" id="demo">
        <span className="sc-tag">Demo</span>
        <h2 className="sc-h2">Sixty seconds of it working</h2>
        <p className="sc-section-sub">
          No slides. A question goes in, an answer comes back with its citation,
          and then a question it can't answer from the documents.
        </p>

        <div className="sc-video">
          <iframe
            src={`https://www.loom.com/embed/${LOOM_ID}`}
            title="Sourced demo"
            frameBorder="0"
            allowFullScreen
          ></iframe>
        </div>

        <p className="sc-video-fallback">
          Video not loading?{" "}
          <a
            href={`https://www.loom.com/share/${LOOM_ID}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open it directly
          </a>
          .
        </p>
      </section>

      <section className="sc-section">
        <span className="sc-tag">Three rules</span>
        <h2 className="sc-h2">
          What makes it usable for <span className="sc-accent">real work</span>
        </h2>
        <p className="sc-section-sub">
          Most document search fails in one of three ways. Sourced is built
          around not failing in those ways.
        </p>

        <div className="sc-grid-3">
          {PRINCIPLES.map((p) => (
            <div className="sc-card" key={p.title}>
              <div className="sc-card-dot"></div>
              <h3 className="sc-card-title">{p.title}</h3>
              <p className="sc-card-body">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="sc-section">
        <span className="sc-tag">Data handling</span>
        <h2 className="sc-h2">How your documents are treated</h2>
        <p className="sc-section-sub">
          The usual reason a firm can't use an AI tool on its own files is that
          the files have to leave the building. These don't.
        </p>

        <div className="sc-grid-2">
          {DATA_POINTS.map((d) => (
            <div className="sc-row" key={d.title}>
              <h3 className="sc-row-title">{d.title}</h3>
              <p className="sc-row-body">{d.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="sc-section">
        <span className="sc-tag">Where it came from</span>
        <h2 className="sc-h2">Built on a document set that couldn't tolerate a wrong answer</h2>

        <div className="sc-origin">
          <p>
            Sourced was built inside a pharmaceutical manufacturer, on their CMO
            contract library. In that environment a confidently wrong answer
            isn't an inconvenience, it's a regulatory problem, and the clause
            that matters is rarely in the document anyone thought to open.
          </p>
          <p>
            That's where the three rules above came from. Cite everything,
            refuse to guess, and surface the right passage even when nobody
            remembers which file it's in. They weren't features added later to
            reassure anyone - the system was unusable without them.
          </p>
        </div>
      </section>

      <div className="sc-rule"></div>

      <section className="sc-section">
        <span className="sc-tag">Free build</span>
        <h2 className="sc-h2">
          Try it on <span className="sc-accent">your own documents</span>
        </h2>
        <p className="sc-section-sub">
          A demo on someone else's files proves very little. This is the part
          that actually tells you whether it's worth anything.
        </p>

        <div className="sc-grid-3">
          {STEPS.map((s) => (
            <div className="sc-step" key={s.n}>
              <span className="sc-step-n">{s.n}</span>
              <h3 className="sc-card-title">{s.title}</h3>
              <p className="sc-card-body">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="sc-close">
        <h2 className="sc-close-title">
          Want one built on your documents?
        </h2>
        <p className="sc-close-sub">
          Tell me roughly what you'd want it to answer and which documents hold
          the answer. I'll come back with what a free build would look like.
        </p>
        <a className="sc-btn-primary sc-btn-lg" href={MAILTO}>
          Email me
        </a>
        <p className="sc-close-mail">
          <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
        </p>
      </section>

      <footer className="sc-footer">
        <p>
          Sourced is built and run by Raja Omar.{" "}
          <a
            href="https://www.linkedin.com/in/rajaomar-dev"
            target="_blank"
            rel="noopener noreferrer"
          >
            LinkedIn
          </a>
        </p>
      </footer>
    </div>
  );
};

export default Assistant;
